import getSymbolFromCurrency from 'currency-symbol-map';
import numeral from 'numeral';

export function noNaN(n: string | number): number {
  return typeof n === 'string' ? Number.parseFloat(n) : (n ?? 0);
}

if (numeral.locales.custom === undefined) {
  numeral.register('locale', 'custom', {
    ...numeral.localeData(),
    abbreviations: {
      thousand: 'K',
      million: 'M',
      billion: 'B',
      trillion: 'T',
    },
  });
}
numeral.locale('custom');

export const ShowSignEnum = Object.freeze({
  NEGATIVE_ONLY: 'negative-only',
  POSITIVE_ONLY: 'positive-only',
  NEVER: 'never',
  ALWAYS: 'always',
});
export type ShowSign = (typeof ShowSignEnum)[keyof typeof ShowSignEnum];

// deprecated: not for direct use
// use hooks/privacyMode/formatAmount or hooks/privacyMode/useFormatAmount instead
export function formatNumber(
  val: number,
  currencySymbol = 'USD',
  fmtString = '0,0',
  showSign: ShowSign = ShowSignEnum.NEGATIVE_ONLY,
) {
  return internalFormatNumber(val, getSymbolFromCurrency(currencySymbol || 'N/A') || '', fmtString, showSign);
}

export function decimalCount(num: number) {
  if (Number.isInteger(num)) {
    return 0;
  } else {
    return num?.toString()?.split('.')[1]?.length || 0;
  }
}

export function round(value: number, decimals = 2) {
  // Tiny values (e.g. float residue like -4.5e-13) stringify in exponent notation, so appending another
  // `e${decimals}` would produce NaN.
  if (`${value || 0}`.includes('e')) {
    const factor = 10 ** decimals;
    return Math.round((value || 0) * factor) / factor || 0;
  }
  const roundedValue = Math.round(Number(`${value || 0}e${decimals}`));
  // `|| 0` normalizes -0 so small negative values don't render as "-$0.00".
  return Number(`${roundedValue}e-${decimals}`) || 0;
}

export function getRoundedInvestmentVal(num: number) {
  let count = decimalCount(num);
  if (count < 2) {
    count = 2;
  } else if (count > 4) {
    count = 4;
  }
  return num.toFixed(count);
}

export function internalFormatNumber(
  val: number,
  currencyString: string,
  fmtString: string,
  showSign: ShowSign = ShowSignEnum.NEGATIVE_ONLY,
  showFractions?: boolean,
  fractionDigits = 2,
) {
  const useFractionalVal = showFractions && Math.abs(val) < 0.01 && val !== 0;
  const value = useFractionalVal ? noNaN(val) : noNaN(round(val, fractionDigits));
  let sign = '';
  switch (showSign) {
    case ShowSignEnum.POSITIVE_ONLY:
      sign = Number(value) > 0 ? '+' : '';
      break;
    case ShowSignEnum.NEVER:
      sign = '';
      break;
    case ShowSignEnum.ALWAYS:
      sign = Number(value) > 0 ? '+' : '-';
      if (Number(value) === 0) sign = '';
      break;
    case ShowSignEnum.NEGATIVE_ONLY:
    default:
      sign = Number(value) < 0 ? '-' : '';
  }
  if (useFractionalVal) {
    return `${sign}${currencyString}${getRoundedInvestmentVal(Math.abs(value))}`;
  }

  return `${sign}${currencyString}${numeral(Math.abs(value)).format(fmtString)}`;
}

export const floatMathAdjuster = (num: number) => Math.round(num * 100) / 100;

export function formatAbbreviatedNumber(value: number, currencySymbol: string = '$'): string {
  const magnitudes = [
    { threshold: 1e12, divisor: 1e12, suffix: 'T' },
    { threshold: 1e9, divisor: 1e9, suffix: 'B' },
    { threshold: 1e6, divisor: 1e6, suffix: 'M' },
    { threshold: 1e3, divisor: 1e3, suffix: 'k' },
  ];
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const magnitude = magnitudes.find((candidate) => absValue >= candidate.threshold);

  if (magnitude) {
    const formatted = (absValue / magnitude.divisor).toFixed(1).replace(/\.0$/, '');
    return `${currencySymbol}${sign}${formatted}${magnitude.suffix}`;
  }

  return `${currencySymbol}${sign}${Math.round(absValue)}`;
}

// return true if the string is a valid integer
export function isIntegerString(str: string): boolean {
  return /^-?\d+$/.test(str);
}
